// ProjectActivity — the Activity tab: the same project-scoped feed as the
// Overview card (rows shared via ProjectActivityList) but full-width and
// load-more paginated. Pages accumulate in a page-keyed map with an id-dedupe
// flatten (the ProjectsPage board idiom; same v1 caveat — an edit landing on
// an earlier page stays stale until the stack resets). Total comes through
// useStickyTotal since the endpoint states it on page 1 only; the head renders
// it via the activity-log's own entries-count key.

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardsLoadMore, Icon } from '@/components/common';
import { useGetActivityLogsQuery, type ActivityItem } from '@/api/endpoints/activityLogApi';
import useStickyTotal from '@/hooks/useStickyTotal';
import ProjectActivityList from './ProjectActivityList';

const PAGE_SIZE = 10;

interface ProjectActivityProps {
  projectId: string;
}

const ProjectActivity = ({ projectId }: ProjectActivityProps) => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState<Map<number, ActivityItem[]>>(new Map());

  const { currentData, isLoading, isFetching, isError } = useGetActivityLogsQuery(
    { projectIds: [projectId], page, limit: PAGE_SIZE },
    { refetchOnMountOrArgChange: true },
  );

  // Defensive — the component remounts on tab switches, but a project change
  // under a mounted tab must restart the accumulated stack.
  useEffect(() => {
    setPage(1);
    setPages(new Map());
  }, [projectId]);

  // Record the page that just arrived — keyed by page, so an invalidation
  // refetch overwrites its own slot.
  useEffect(() => {
    if (currentData) {
      setPages((prev) => new Map(prev).set(page, currentData.items));
    }
  }, [currentData, page]);

  // Flatten the accumulated pages in page order with an id-dedupe (a refetched
  // boundary can briefly hold a row twice).
  const items = useMemo(() => {
    const seen = new Set<string>();
    const flat: ActivityItem[] = [];
    [...pages.keys()]
      .sort((a, b) => a - b)
      .forEach((key) => {
        pages.get(key)?.forEach((item) => {
          if (!seen.has(item.id)) {
            seen.add(item.id);
            flat.push(item);
          }
        });
      });
    return flat;
  }, [pages]);

  const total = useStickyTotal(currentData?.total, page);
  const hasMore = items.length > 0 && items.length < total;
  const loadingMore = isFetching && page > 1;
  const loading = items.length === 0 && (isLoading || isFetching);

  return (
    <Card
      padded={false}
      className='project-activity'
      head={
        <span className='project-activity__head'>
          <Icon name='scroll-text' size={15} />
          <b className='project-activity__title'>{t('projects_detail_activity')}</b>
          <span className='project-activity__count'>
            {t('activity_log_entries_count', { count: total })}
          </span>
        </span>
      }
    >
      {loading ? (
        <p className='project-detail__empty'>{t('text_loading')}</p>
      ) : isError && items.length === 0 ? (
        <p className='project-detail__empty'>
          <Icon name='circle-alert' size={26} />
          {t('error_generic')}
        </p>
      ) : items.length === 0 ? (
        <p className='project-detail__empty'>{t('projects_activity_empty')}</p>
      ) : (
        <>
          <ProjectActivityList items={items} />
          {hasMore && (
            <div className='project-activity__more'>
              <CardsLoadMore
                hasMore={hasMore}
                loading={loadingMore}
                onLoadMore={() => setPage((p) => p + 1)}
                label={t('text_load_more')}
              />
            </div>
          )}
        </>
      )}
    </Card>
  );
};

export default ProjectActivity;
